
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import StrategyDetails from "@/components/follow-strategy/StrategyDetails";
import StrategyNotFound from "@/components/follow-strategy/StrategyNotFound";
import PremiumAdvantages from "@/components/follow-strategy/PremiumAdvantages";
import PremiumCheckout from "@/components/follow-strategy/PremiumCheckout";
import { getPremiumAdvantages } from "@/utils/strategyDataUtils";
import { supabase } from "@/integrations/supabase/client";
import ChatOption from "@/components/ChatOption";
import { useIsMobile } from "@/hooks/use-mobile";

const FollowStrategy = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(true);
  const [hasSubscription, setHasSubscription] = useState(false);

  const strategy = location.state?.strategy;
  const filters = location.state?.filters;
  const originalStrategyId = location.state?.originalStrategyId;

  // Check if user already has a subscription
  useEffect(() => {
    const checkSubscription = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
          setIsCheckingSubscription(false);
          return;
        }

        // Check for any valid subscription (active OR cancelled but still valid)
        const { data: subs, error } = await supabase
          .from('strategy_subscriptions')
          .select('*')
          .eq('user_id', user.id);

        if (error) {
          console.error("Error checking subscription:", error);
          setIsCheckingSubscription(false);
          return;
        }

        // Check both active subscriptions AND those with future billing dates
        if (subs && subs.length > 0) {
          const currentDate = new Date();
          const hasValidSubscription = subs.some(sub => {
            const nextBillingDate = sub.next_billing_date ? new Date(sub.next_billing_date) : null;
            return sub.active || (nextBillingDate && currentDate < nextBillingDate);
          });

          setHasSubscription(hasValidSubscription);
        }
      } catch (error) {
        console.error("Error checking subscription:", error);
      } finally {
        setIsCheckingSubscription(false);
      }
    };

    checkSubscription();
  }, []);

  // Get advantages from utility function
  const advantages = getPremiumAdvantages();

  if (!strategy) {
    return <StrategyNotFound filters={filters} />;
  }

  // Make sure isPremium is set correctly if it wasn't in the incoming data
  const enrichedStrategy = {
    ...strategy,
    isPremium: strategy.isPremium !== undefined ? strategy.isPremium : true
  };

  // If the user already has a subscription, navigate them to the thank you page
  const handleExistingSubscription = () => {
    navigate('/thank-you', {
      state: {
        strategy: enrichedStrategy,
        isYearly: true // Default to yearly
      }
    });
  };

  const isMobile = useIsMobile();

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E7F0FD] to-white">
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <button
          onClick={() => navigate('/marketplace', { state: { filters } })}
          className="flex items-center text-[#517FA4] hover:text-[#243949] mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </button>

        <div className="bg-white rounded-xl shadow-lg p-8">
          <h1 className="text-3xl font-bold text-[#243949] mb-8">
            Follow {enrichedStrategy.name}
          </h1>

          <div className="grid md:grid-cols-2 gap-12">
            <StrategyDetails strategy={enrichedStrategy} />

            <div>
              <PremiumAdvantages advantages={advantages} />

              {isCheckingSubscription ? (
                <div className="text-center p-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#517FA4] mx-auto"></div>
                  <p className="mt-2 text-[#517FA4]">Checking subscription status...</p>
                </div>
              ) : hasSubscription ? (
                <div className="bg-green-50 p-4 rounded-lg mb-6">
                  <h3 className="font-semibold text-green-700">You already have a Premium subscription!</h3>
                  <p className="text-green-600 mb-4">You can follow this strategy without additional payment.</p>
                  <button
                    onClick={handleExistingSubscription}
                    className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg transition-colors"
                  >
                    Follow this Strategy
                  </button>
                </div>
              ) : (
                <PremiumCheckout
                  strategy={enrichedStrategy}
                  advantages={advantages}
                />
              )}
            </div>
          </div>
        </div>
      </main>
      {/* Only show ChatOption on desktop */}
      {!isMobile && (
        <ChatOption />
      )}
      <Footer />
    </div>
  );
};

export default FollowStrategy;
